/**
 * Shared date/time display for feed posts, feed comments, and profile movie rows.
 * Relative labels + detailed tooltip (same rules everywhere).
 */

const MS_MIN = 60 * 1000;
const MS_HOUR = 60 * MS_MIN;

function coerceDate(isoOrDate) {
  if (isoOrDate instanceof Date) {
    return Number.isNaN(isoOrDate.getTime()) ? null : isoOrDate;
  }
  if (isoOrDate == null || isoOrDate === '') return null;
  const d = new Date(isoOrDate);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Time only, e.g. 3:45 PM */
export function formatTimeOfDay(d) {
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(d);
}

/** e.g. 3 Apr 2026 at 3:45 PM */
export function formatDateAtTime(isoOrDate) {
  const d = coerceDate(isoOrDate);
  if (!d) return '';
  const datePart = new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(d);
  return `${datePart} at ${formatTimeOfDay(d)}`;
}

/** e.g. Friday 3 April 2026 at 3:45 PM — for title / aria-label */
export function formatDetailedTimestamp(isoOrDate) {
  const d = coerceDate(isoOrDate);
  if (!d) return '';
  const weekday = new Intl.DateTimeFormat('en-GB', {
    weekday: 'long',
  }).format(d);
  const day = d.getDate();
  const month = new Intl.DateTimeFormat('en-GB', { month: 'long' }).format(d);
  const year = d.getFullYear();
  return `${weekday} ${day} ${month} ${year} at ${formatTimeOfDay(d)}`;
}

/**
 * Short relative label: Just now, Nm, Nh, Yesterday at …, else date at time.
 * @param {string|Date|null|undefined} isoOrDate
 * @param {number} [nowMs=Date.now()]
 */
export function formatRelativeTime(isoOrDate, nowMs = Date.now()) {
  const d = coerceDate(isoOrDate);
  if (!d) return '';
  const diffMs = nowMs - d.getTime();
  if (diffMs < 0) return formatDateAtTime(d);

  const diffMins = Math.floor(diffMs / MS_MIN);
  const diffHours = Math.floor(diffMs / MS_HOUR);

  if (diffMins < 60) {
    if (diffMins < 1) return 'Just now';
    return `${diffMins}m`;
  }
  if (diffHours < 24) {
    return `${diffHours}h`;
  }
  if (diffHours < 48) {
    return `Yesterday at ${formatTimeOfDay(d)}`;
  }
  return formatDateAtTime(d);
}
